import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.`init`.InitKobwebContext
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import tools.goodtime.components.layouts.MarkdownLayout as _tools_goodtime_components_layouts_MarkdownLayout
import tools.goodtime.components.layouts.PageLayout as _tools_goodtime_components_layouts_PageLayout
import tools.goodtime.pages.HomePage as _tools_goodtime_pages_HomePage
import tools.goodtime.pages.LegalPage as _tools_goodtime_pages_LegalPage
import tools.goodtime.pages.NotFoundPage as _tools_goodtime_pages_NotFoundPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Goodtime Tools"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "tools.goodtime.components.layouts.MarkdownLayout",
            parentLayoutId = "tools.goodtime.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                tools.goodtime.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _tools_goodtime_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "tools.goodtime.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _tools_goodtime_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "tools.goodtime.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                tools.goodtime.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _tools_goodtime_pages_HomePage()
        }
        ctx.router.register(
            "/404",
        ) { pageCtx -> 
            _tools_goodtime_pages_NotFoundPage()
        }
        ctx.router.register(
            "/legal",
            layoutId = "tools.goodtime.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                tools.goodtime.pages.initLegalPage(ctx)
            },
        ) { pageCtx -> 
            _tools_goodtime_pages_LegalPage()
        }

        tools.goodtime.initErrorPage(ctx)
    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("hero-button", tools.goodtime.HeroButton)
        ctx.theme.registerStyle("features-button", tools.goodtime.FeaturesButton)
        ctx.theme.registerStyle("markdown", tools.goodtime.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", tools.goodtime.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("features-link", tools.goodtime.components.sections.FeaturesLinkStyle)
        ctx.theme.registerStyle("screenshots", tools.goodtime.components.sections.ScreenshotsStyle)
        ctx.theme.registerStyle("nav-header-background", tools.goodtime.components.sections.NavHeaderBackgroundStyle)
        ctx.theme.registerStyle("nav-header", tools.goodtime.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("footer", tools.goodtime.components.sections.FooterStyle)
        ctx.theme.registerStyle("footer-link", tools.goodtime.components.sections.FooterLinkStyle)
        ctx.theme.registerStyle("rating-text", tools.goodtime.components.sections.RatingTextStyle)
        ctx.theme.registerStyle("gradient-box", tools.goodtime.widgets.GradientBoxStyle)
        ctx.theme.registerVariant("-circle", tools.goodtime.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", tools.goodtime.UncoloredButtonVariant
        )
        ctx.theme.registerVariant("-variant70", tools.goodtime.widgets.GradientBoxVariant70
        )
        ctx.theme.registerVariant("-primary", tools.goodtime.widgets.PrimaryButtonVariant
        )
        ctx.theme.registerVariant("-normal", tools.goodtime.widgets.NormalButtonVariant
        )
        ctx.theme.registerVariant("-unsized-button-style", tools.goodtime.widgets.UnsizedButtonStyle
        )
        ctx.theme.registerVariant("-unstyled", tools.goodtime.widgets.UnstyledButtonVariant
        )
        tools.goodtime.initColorMode(ctx)
        tools.goodtime.initStyles(ctx)
        tools.goodtime.initSiteStyles(ctx)
        tools.goodtime.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo("/" + BasePath.remove(window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        tools.goodtime.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
